import _ from 'underscore';

import {
  getUsedOptionalComponentTypes,
  insertNewComponents,
  componentsConfig,
} from '@bingads-webui-campaign-react/smart-component-util';
import { applyBookAppointmentDefaultConfig, SmartActionButtonTypes } from '@bingads-webui-campaign-react/smart-component-action-button';

import { SmartPageTemplate, TemplateComponentListMapping } from '../consts';

/**
* Only add components, and not handle removing components if the template is different
* @param {Array} components Current component configs.
* @param {Array} suggestions List of suggestion template
* @param {Object} pilots pilots values
* @returns {Array} New components
*/
export const applyTemplateSuggestion = (components, suggestions, pilots) => {
  const addedOptionalCompTypes = getUsedOptionalComponentTypes(components);
  // Apply Suggestion
  let templateCompList = [];
  // Apply book appointment CTA when true and apply get quotes when false
  const hasProfessionalTemplate = suggestions.includes(SmartPageTemplate.Professional);

  // 1. Get all the component mapping from suggested template
  _.each(suggestions, (sug) => {
    const componentList = TemplateComponentListMapping[sug] || [];
    templateCompList.push(...componentList);
  });

  // 2. Filter out required components and uniq
  templateCompList = _.chain(templateCompList)
    .filter(config => !config.required)
    .uniq(config => config.type)
    .value();

  // 3. Add components which aren't presented yet in current config
  const templateCompListToAdd = _.filter(
    templateCompList,
    config => !addedOptionalCompTypes.includes(config.type)
  );

  const componentsAfterAdd = insertNewComponents(
    components,
    _.map(templateCompListToAdd, c => c.type)
  );

  // 4. Do some special treatment
  // Here we overwrite CTA to booking when using professional template
  // CTA is required, so the index will always > -1
  const ctaConfigIndex = _.findIndex(
    componentsAfterAdd,
    c => c.type === componentsConfig.callToAction.type
  );

  if (
    hasProfessionalTemplate
    && componentsAfterAdd[ctaConfigIndex].actionType !== SmartActionButtonTypes.BookAppointment.name
  ) {
    applyBookAppointmentDefaultConfig(componentsAfterAdd[ctaConfigIndex], pilots);
  }

  return componentsAfterAdd;
};

/**
 * Mutate the original object and apply template suggestion to it
 * @param {Object} siteConfig siteConfig which will be modified
 * @param {Object} pilots pilot object map
 * @param {Function} getTemplateSuggestions helper to get template suggestions
 * @param {Object} logger logger object for metrics
 * @return {Promise} Promise result for chaining
 */
export const templateSuggestionProxy = (siteConfig, pilots, getTemplateSuggestions, logger) => {
  const { isSmartPageTemplateSuggestionEnabled } = pilots;

  if (!isSmartPageTemplateSuggestionEnabled) {
    return siteConfig;
  }

  const {
    dataSource: { businessCategories = [] },
    pages,
  } = siteConfig;

  logger.logTrace('Getting template suggestion');

  return getTemplateSuggestions(businessCategories)
    .then((suggestions) => {
      const { components } = pages[0];
      pages[0].components = applyTemplateSuggestion(components, suggestions, pilots);

      const hasProfessionalTemplate = suggestions.includes(SmartPageTemplate.Professional);
      const { siteContextInfo = {} } = siteConfig;

      if (
        hasProfessionalTemplate
        && _.isUndefined(siteContextInfo.shouldShowProfessionalTemplateGuidance)
      ) {
        // eslint-disable-next-line no-param-reassign
        siteConfig.siteContextInfo = {
          ...siteConfig.siteContextInfo,
          shouldShowProfessionalTemplateGuidance: true,
        };
      }

      // Config is modified via reference
      return siteConfig;
    })
    .catch((err) => {
      logger.logError('Error while getting template suggestion', err);

      return siteConfig;
    })
    .finally(() => logger.logTrace('Finished template suggestion'));
};
